import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CompPagination, Container } from "components";
import { AiOutlinePlus } from "react-icons/ai";
import Breadcrumb from "components/molecules/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { API_URL_department } from "constants/api";
import { departmentReducers } from "redux/departmentSlice";
import { SyncLoader } from "react-spinners";
import { deleteData, getData } from "actions";
import useDebounce from "hooks/useDebounce";
import { BiSortDown, BiSortUp } from "react-icons/bi";
import { FaEdit, FaTrash } from "react-icons/fa";
import { AuthContext } from "context/AuthContext";

// field for use sort up/down
const TABLE_HEAD = [
  { title: "Name", field: "name" },
  { title: "Type", field: "type" },
  { title: "Sla Plan", field: "sla_name" },
  { title: "Department Manager", field: "manager_name" },
  { title: "Actions", field: "" },
];

const Departments = () => {
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  const {
    getDepartmentResult,
    getDepartmentLoading,
    getDepartmentError,
    addDepartmentResult,
    deleteDepartmentResult,
  } = useSelector((state) => state.department);

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_department,
        params,
        { dispatch, redux: departmentReducers },
        "GET_DEPARTMENT"
      );
    },
    [dispatch]
  );

  const onDelete = (item) => {
    deleteData(
      API_URL_department + item.id,
      { dispatch, redux: departmentReducers },
      "DELETE_DEPARTMENT"
    );
  };

  const onEdit = (item) => {
    navigate(`/administrator/staff/departments/edit`, {
      state: {
        item,
      },
    });
  };

  const handlePageClick = ({ selected }) => {
    const newOffset = selected * limit;
    const params = `??all=True&limit=${limit}&offset=${newOffset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    setOffset(newOffset);
    setCurrentPage(selected);
    get(params);
  };

  const delayedSearch = useCallback(
    useDebounce((value) => {
      //RESET PAGINATE KE 1
      setOffset(0);
      setCurrentPage(0);
      const params = `??all=True&limit=${limit}&offset=${offset}&search=${value}&ordering=${
        sortOrder === "desc" ? "-" : ""
      }${sortColumn}`;
      get(params);
    }, 1000),
    [limit, offset, sortOrder, sortColumn]
  );

  const onSearch = (value) => {
    setSearch(value);
    delayedSearch(value);
  };

  const handleSort = (column) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);
    if (column === sortColumn) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Sort icons
  const renderSortIcon = (field) => {
    if (field === sortColumn) {
      return sortOrder === "asc" ? (
        <BiSortUp className="ml-1 h-4" />
      ) : (
        <BiSortDown className="ml-1 h-4" />
      );
    }
    return <BiSortUp className="ml-1 h-4 text-gray-300" />;
  };

  const handleSelectLimit = (limit) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);

    const params = `??all=True&limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
    setLimit(limit);
  };

  const action = [
    {
      name: "Edit",
      icon: <FaEdit />,
      func: onEdit,
      colorlight: "bg-sky-500",
      colordark: "dark:bg-sky-800",
      hoverlight: "hover:bg-sky-600",
      hoverdark: "hover:dark:bg-sky-900",
    },
    {
      name: "Delete",
      icon: <FaTrash />,
      func: onDelete,
      colorlight: "bg-red-500",
      colordark: "dark:bg-red-800",
      hoverlight: "hover:bg-red-600",
      hoverdark: "hover:dark:bg-red-900",
    },
  ];

  const fetchData = useCallback(() => {
    const params = `?all=True&limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
  }, [get, limit, offset, sortColumn, sortOrder]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [limit, offset, sortColumn, sortOrder]);

  useEffect(() => {
    if (addDepartmentResult) {
      fetchData();
    }
  }, [addDepartmentResult, fetchData]);

  useEffect(() => {
    if (deleteDepartmentResult) {
      fetchData();
    }
  }, [deleteDepartmentResult, fetchData]);

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Departments"}
        excludedPaths={["staff", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          List of departments
          <div className="col-end-7 flex justify-end">
            <Link
              to="/administrator/staff/departments/create"
              className="flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
            >
              <AiOutlinePlus />
              Create a department
            </Link>
          </div>
        </div>

        <div className="flex flex-col md:flex-row text-center md:justify-between align-middle px-4 my-4 bg-neutral dark:bg-neutral-800 ">
          <div className="p-1 inline-block align-middle">
            <select
              id="limit"
              value={limit}
              onChange={(e) => handleSelectLimit(e.target.value)}
              className="border rounded-sm p-1 border-gray-300 dark:text-black focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
            <span className="mx-2 text-sm ">Records per page</span>
          </div>
          <div className="p-1 inline-block align-middle">
            <span className="text-sm mr-2">Search:</span>
            <input
              id="search"
              type="text"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              className="p-1 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>

        {/* <CardContainer title="List of agents"> */}
        <div className="mx-4">
          <div className="overflow-x-auto min-h-[55vh]">
            <table className="border border-collapse items-center w-full">
              <thead className="text-left">
                <tr>
                  {TABLE_HEAD.map((item, itemIdx) => (
                    <th
                      key={itemIdx}
                      className="p-2 border text-sm whitespace-nowrap cursor-pointer"
                      onClick={() => {
                        item.field && handleSort(item.field);
                      }}
                    >
                      <span className="flex">
                        {item.title}
                        {item.field && renderSortIcon(item.field)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* loading  */}
                {getDepartmentLoading && (
                  <tr>
                    <td
                      className="text-center py-12"
                      colSpan={TABLE_HEAD.length + 1}
                    >
                      <div className="pt-10 pb-6 flex justify-center items-center">
                        <SyncLoader color="#4477CE" />
                      </div>
                    </td>
                  </tr>
                )}

                {/* Error */}
                {getDepartmentError && (
                  <tr>
                    <td className="text-center" colSpan={TABLE_HEAD.length + 1}>
                      <div className="pt-20 pb-12 flex justify-center items-center text-xs text-red-500">
                        {getDepartmentError}
                      </div>
                    </td>
                  </tr>
                )}

                {/* Result = 0 */}
                {getDepartmentResult.results &&
                  getDepartmentResult.results.length === 0 && (
                    <tr>
                      <td
                        className="text-center"
                        colSpan={TABLE_HEAD.length + 1}
                      >
                        <div className="pt-20 pb-12 flex justify-center items-center text-xs text-slate-600 dark:text-white">
                          No Data
                        </div>
                      </td>
                    </tr>
                  )}

                {getDepartmentResult.results &&
                  getDepartmentResult.results.map((item, index) => (
                    <tr key={index}>
                      <td className="p-4 border border-blue-gray-50 w-1/5">
                        <button
                          className="text-blue-700 dark:text-blue-300"
                          onClick={() => onEdit(item)}
                        >
                          {item.name}
                        </button>
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-1/5">
                        <p
                          className={`${
                            item.type === 1 ? "text-red-600" : "text-green-600"
                          } `}
                        >
                          {item.type === 1 ? "Private" : "Public"}
                        </p>
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-1/5">
                        {item.sla_name}
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-1/5">
                        {item.manager_name}
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-1/5">
                        <div className="flex">
                          {action.map((action, actionIdx) => (
                            <button
                              key={actionIdx}
                              className={`mx-0.5 text-white text-sm ${action.colorlight} ${action.colordark} ${action.hoverlight} ${action.hoverdark} rounded p-1 `}
                              onClick={() => action.func(item)}
                            >
                              <div className="flex items-center">
                                {action.icon} {action.name}
                              </div>
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="pt-5 py-11">
            <CompPagination
              handlePageClick={(e) => handlePageClick(e)}
              pageCount={
                getDepartmentResult.count > 0 ? getDepartmentResult.count : 0
              }
              limit={limit}
              offset={offset}
              currentPage={currentPage}
            />
          </div>
        </div>
        {/* </CardContainer> */}
      </div>
    </Container>
  );
};

export default Departments;
